'use memo'

import { REGISTRATION_DIVISION_PARAM } from '@sportninja/common/features/registration/registrationUrlParams'
import {
  findPreselectableDivision,
  type PreselectableDivision,
} from '../utils/preselectDivision'
import { usePreselectFromUrl } from './usePreselectFromUrl'

interface Params<T extends PreselectableDivision> {
  /** The divisions the step actually renders — i.e. after filtering. */
  divisions: T[] | null | undefined
  /** The current selection — a pre-select only ever fills an empty step. */
  selectedDivision: T | null
  onSelect: (division: T) => void
}

/**
 * Pre-select the division a link points at (`?division_id=`).
 *
 * Highlights the card only — the reader still presses Continue, so they see
 * which division they are registering for and Back from the next step behaves
 * normally instead of shoving them forward again.
 */
export const usePreselectedDivision = <T extends PreselectableDivision>({
  divisions,
  selectedDivision,
  onSelect,
}: Params<T>): void => {
  usePreselectFromUrl<T>({
    param: REGISTRATION_DIVISION_PARAM,
    // Deliberately NOT gated on the session's own division, matching the team
    // pre-select: following a link that names one is an explicit choice and
    // outranks what an earlier pass left on the session. The latch in
    // `usePreselectFromUrl` still applies it once and then stands aside.
    enabled: Boolean(divisions?.length) && !selectedDivision,
    resolve: (divisionId) => findPreselectableDivision(divisions, divisionId),
    onSelect,
  })
}
